import { useState, useEffect } from "react";
import { format } from "date-fns";
import { UtensilsCrossed, CalendarPlus, Building2, MapPin, FileText, Paperclip, Upload, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PhoneLink, EmailLink, AddressLink } from "@/components/marketing/contact-links";
import { ActivityDetailDialog } from "@/components/marketing/ActivityDetailDialog";
import { AddContactDialog } from "@/components/marketing/AddContactDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AddCateringEventDialog({ open, onOpenChange, onAdd, cateringLeads }) {
  const empty = { lead_id: "", business_name: "", date: format(new Date(), "yyyy-MM-dd"),
    setup_time: "", event_time: "", end_time: "", event_location: "", result: "", status: "Planned", receipt_url: "" };
  const [f, setF] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setF(empty); /* eslint-disable-next-line */ }, [open]);

  const pickLead = (id) => {
    const l = cateringLeads.find((x) => x.id === id);
    setF((p) => ({ ...p, lead_id: id, business_name: l?.business_name || p.business_name, event_location: l?.address || p.event_location }));
  };
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const uploadReceipt = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, receipt_url: data.url }));
      toast.success("Receipt attached");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); ev.target.value = ""; }
  };

  const submit = async () => {
    if (!f.business_name.trim()) return;
    setSaving(true);
    try {
      await onAdd({
        lead_id: f.lead_id || null, business_name: f.business_name.trim(),
        scheduled_at: new Date(`${f.date}T${f.event_time || "09:00"}:00`).toISOString(),
        category: "catering", status: f.status,
        setup_time: f.setup_time, event_time: f.event_time, end_time: f.end_time,
        event_location: f.event_location, result: f.result || null, receipt_url: f.receipt_url || null,
        actions_taken: [], offers_given: [],
      });
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Catering Event</DialogTitle><DialogDescription>Schedule a catering order/event with logistics and receipt.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {cateringLeads.length > 0 && (
            <div className="space-y-2"><Label>Catering Contact</Label>
              <Select value={f.lead_id} onValueChange={pickLead}>
                <SelectTrigger data-testid="catering-lead"><SelectValue placeholder="Select a contact (optional)" /></SelectTrigger>
                <SelectContent>{cateringLeads.map((l) => <SelectItem key={l.id} value={l.id}>{l.business_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2"><Label>Business / Event Name *</Label>
            <Input value={f.business_name} data-testid="catering-business" onChange={set("business_name")} required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Date</Label><Input type="date" value={f.date} data-testid="catering-date" onChange={set("date")} /></div>
            <div className="space-y-2"><Label>Event Location</Label><Input value={f.event_location} onChange={set("event_location")} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2"><Label>Setup</Label><Input type="time" value={f.setup_time} onChange={set("setup_time")} /></div>
            <div className="space-y-2"><Label>Event</Label><Input type="time" value={f.event_time} onChange={set("event_time")} /></div>
            <div className="space-y-2"><Label>End</Label><Input type="time" value={f.end_time} onChange={set("end_time")} /></div>
          </div>
          <div className="space-y-2"><Label>Order Details / Notes</Label><Textarea value={f.result} onChange={set("result")} rows={3} /></div>
          <div className="space-y-2">
            <Label>Receipt</Label>
            {f.receipt_url ? (
              <div className="flex items-center justify-between border px-3 py-2 rounded-md bg-muted/40">
                <a href={`${BACKEND_URL}${f.receipt_url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline"><FileText className="h-4 w-4" /> View receipt</a>
                <button type="button" onClick={() => setF((p) => ({ ...p, receipt_url: "" }))} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 border border-dashed rounded-md px-3 py-3 text-sm text-muted-foreground cursor-pointer hover:bg-muted/40">
                {uploading ? <><Upload className="h-4 w-4 animate-pulse" /> Uploading…</> : <><Paperclip className="h-4 w-4" /> Attach receipt</>}
                <input data-testid="catering-receipt-input" type="file" className="hidden" onChange={uploadReceipt} disabled={uploading} />
              </label>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input id="cat-completed" type="checkbox" checked={f.status === "Completed"} onChange={(e) => setF((p) => ({ ...p, status: e.target.checked ? "Completed" : "Planned" }))} />
            <Label htmlFor="cat-completed" className="cursor-pointer">Mark as completed</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="save-catering-event" className="bg-orange-600 hover:bg-orange-700" disabled={saving || !f.business_name.trim()}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CateringModule({ activitiesApi, targetsApi, storeCode }) {
  const { activities, addActivity, updateActivity, completeActivity, deleteActivity } = activitiesApi;
  const { targets, subtypes, getSubtypesForTarget, leads, addLead } = targetsApi;
  const [filter, setFilter] = useState("upcoming");
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const cateringActivities = activities.filter((a) => a.category === "catering");
  const cateringLeads = leads.filter((l) => l.is_catering);
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const upcomingCount = cateringActivities.filter((a) => new Date(a.date) >= today && a.status !== "Completed").length;
  const completedCount = cateringActivities.filter((a) => a.status === "Completed").length;

  const filtered = cateringActivities.filter((a) => {
    const d = new Date(a.date); d.setHours(0, 0, 0, 0);
    if (filter === "upcoming") return d >= today && a.status !== "Completed";
    if (filter === "completed") return a.status === "Completed";
    return true;
  }).sort((a, b) => filter === "upcoming" ? new Date(a.date) - new Date(b.date) : new Date(b.date) - new Date(a.date));

  return (
    <Card className="card-shadow border-0">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold flex items-center gap-2"><UtensilsCrossed className="h-5 w-5 text-orange-600" /> Catering</CardTitle>
          <Button className="bg-orange-600 hover:bg-orange-700 gap-2" data-testid="new-catering-event" onClick={() => setAddOpen(true)}><CalendarPlus className="h-4 w-4" /> New Catering Event</Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="events">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="events" data-testid="catering-events-tab">Catering Events{upcomingCount > 0 && <span className="ml-2 bg-orange-600 text-white text-xs px-1.5 py-0.5 rounded-full">{upcomingCount}</span>}</TabsTrigger>
            <TabsTrigger value="clients" data-testid="catering-contacts-tab">Contacts</TabsTrigger>
          </TabsList>
          <TabsContent value="events">
            <div className="flex justify-center mb-4">
              <ToggleGroup type="single" value={filter} onValueChange={(v) => v && setFilter(v)} className="bg-muted rounded-lg p-1">
                <ToggleGroupItem value="all" size="sm" className="px-4" data-testid="cat-filter-all">All ({cateringActivities.length})</ToggleGroupItem>
                <ToggleGroupItem value="upcoming" size="sm" className="px-4" data-testid="cat-filter-upcoming">Upcoming ({upcomingCount})</ToggleGroupItem>
                <ToggleGroupItem value="completed" size="sm" className="px-4" data-testid="cat-filter-completed">Completed ({completedCount})</ToggleGroupItem>
              </ToggleGroup>
            </div>
            {filtered.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground"><CalendarPlus className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>No {filter !== "all" ? filter : ""} catering events</p><p className="text-sm">Click "New Catering Event" to schedule one</p></div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto" data-testid="catering-events-list">
                {filtered.map((a) => (
                  <div key={a.id} data-testid={`catering-event-${a.id}`} className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/20 cursor-pointer hover:bg-orange-500/20 transition-colors" onClick={() => { setDetail(a); setDetailOpen(true); }}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-orange-700">{a.business_name}</h4>
                        <div className="mt-2 text-sm text-muted-foreground">
                          <span>{format(new Date(a.date), "MM/dd/yyyy")}</span>
                          {a.setup_time && <span className="ml-2">• Setup: {a.setup_time}</span>}
                          {a.event_time && <span className="ml-2">• Event: {a.event_time}{a.end_time ? `–${a.end_time}` : ""}</span>}
                        </div>
                        {a.event_location && <div className="mt-1 text-sm flex items-center gap-1 text-muted-foreground"><MapPin className="h-3 w-3" /><AddressLink address={a.event_location} className="text-sm" /></div>}
                        {a.result && <p className="text-sm mt-2 line-clamp-2">{a.result}</p>}
                        {a.receipt_url && <div className="flex items-center gap-1 mt-2 text-sm text-orange-600"><FileText className="h-3 w-3" /> Receipt attached</div>}
                      </div>
                      <Badge className={a.status === "Completed" ? "bg-green-600 text-white" : "bg-orange-600 text-white"}>{a.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="clients">
            <div className="flex justify-end mb-4">
              <AddContactDialog targets={targets} subtypes={subtypes} getSubtypesForTarget={getSubtypesForTarget} onAddLead={addLead} context="catering" />
            </div>
            {cateringLeads.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground"><UtensilsCrossed className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>No catering contacts yet</p><p className="text-sm">Add a contact or log a catering event to get started.</p></div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {cateringLeads.map((lead) => {
                  const count = cateringActivities.filter((a) => a.lead_id === lead.id).length;
                  return (
                    <div key={lead.id} className="p-4 rounded-lg border bg-card hover:border-orange-500/30 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1"><Building2 className="h-4 w-4 text-orange-600" /><h4 className="font-semibold">{lead.business_name}</h4></div>
                          <p className="text-sm text-muted-foreground mb-2">{lead.contact_person}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                            <PhoneLink phone={lead.phone} className="text-xs" /><EmailLink email={lead.email} className="text-xs" />
                            <div className="sm:col-span-2"><AddressLink address={lead.address} className="text-xs" /></div>
                          </div>
                        </div>
                        <span className="text-xs bg-orange-500/10 text-orange-600 px-2 py-1 rounded-full font-medium ml-4">{count} events</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      <AddCateringEventDialog open={addOpen} onOpenChange={setAddOpen} onAdd={addActivity} cateringLeads={cateringLeads} />
      <ActivityDetailDialog activity={detail} open={detailOpen} onOpenChange={setDetailOpen}
        onUpdate={updateActivity} onDelete={deleteActivity} onComplete={completeActivity} />
    </Card>
  );
}
